import type { ContestStatus } from '@s2w/types';
import {
  CONTEST_LIFECYCLE_STATES,
  contestLifecycleState,
  toContestLifecycleState,
  underlyingContestStatus,
  type ContestLifecycleState,
  type LifecycleContest as CoreLifecycleContest,
} from '@s2w/core';
import { formatDuration } from '../utils/time';

/**
 * The five states the design speaks in, which the backend's eight
 * `ContestStatus` values collapse onto.
 *
 * **The collapse itself lives in `@s2w/core`**, as `contest-lifecycle.ts`, and
 * this module re-exports it under the names the admin has always used. It moved
 * there on CAE-1571, when the backend began counting and filtering by these same
 * states: the chip counts and the list the chip produces are now answered by two
 * different pieces of code, and a second copy of the decision table would let
 * them disagree. What stays here is everything that *presents* a state — the
 * labels, the chip row, the pill's colour, the milestone track — none of which
 * the backend has any use for.
 *
 * `cancelled` is the fifth, settled on CAE-1541 against the handoff's four: a
 * cancelled contest is not a closed one, and the Over/Under admin draws it with
 * its own red pill rather than a "Cancelled" tag over `closed`.
 */
export const LIFECYCLE_STATES = CONTEST_LIFECYCLE_STATES;

export type { ContestLifecycleState };

/**
 * Everything the lifecycle layer reads off a contest, named structurally so
 * `ContestSummary` and `ContestDetail['contest']` both satisfy it without this
 * module importing the API shapes.
 *
 * Wider than the core module's, which needs only the two status fields. The
 * four dates are what the time cell and the Schedule rail read, and those are
 * screen concerns.
 */
export interface LifecycleContest extends CoreLifecycleContest {
  createdAt: string;
  opensAt: string;
  closesAt: string | null;
  statusChangedAt: string;
}

export interface ContestLifecycle {
  state: ContestLifecycleState;
  /**
   * Pause is an overlay, not a state: `state` still says where the contest is,
   * and the screen adds a separate "Paused" tag beside it at 0.85 opacity.
   *
   * The pill is the exception — it drains to `draft`'s slate while paused. Read
   * it from `statusPillState`, not from `state`.
   */
  isPaused: boolean;
}

/**
 * One backend status to one design state — `@s2w/core`'s table, under the name
 * this app calls it by.
 */
export const toLifecycleState = toContestLifecycleState;

/**
 * The status a contest is really in, seeing through a pause — `@s2w/core`'s
 * unwrap, under the name this app calls it by.
 *
 * Re-exported rather than inlined because `closed` is two backend statuses that
 * read differently to an operator: `settling` is still working, `complete` has
 * finished, and a card that says something about only one of them has to be
 * able to tell them apart.
 */
export const underlyingStatus = (contest: LifecycleContest): ContestStatus =>
  underlyingContestStatus(contest);

export const toLifecycle = (contest: LifecycleContest): ContestLifecycle => ({
  state: contestLifecycleState(contest),
  isPaused: contest.status === 'paused',
});

/**
 * The statuses the backend will pause *from*.
 *
 * Domain knowledge, so it lives here rather than in `pages/contests` — which
 * re-exports it for the detail page's Pause button and the list's row actions.
 * It is the only gate on which rows carry a Pause control; the backend holds
 * the same list and refuses anything else.
 */
export const PAUSABLE_STATUSES: readonly ContestStatus[] = [
  'upcoming',
  'open',
  'in_play',
  'settling',
];

/**
 * What each state is called on screen — the single source for the pill, the
 * filter chips and any prose that names a state.
 *
 * `scheduled` reads **Upcoming**. The internal name follows the design's
 * lifecycle vocabulary; the label follows the backend's own `upcoming` status
 * and the Contests list's chip row, so an operator never sees one contest
 * described two ways.
 */
export const LIFECYCLE_LABELS: Record<ContestLifecycleState, string> = {
  draft: 'Draft',
  scheduled: 'Upcoming',
  live: 'Live',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

/**
 * The state the **pill** wears, which is not always the state the contest is in.
 *
 * A paused contest wears `draft`'s slate. That is the handoff's rule, not an
 * invention here: `01-contests-list/contests-list-app.jsx` renders
 * `<MStatusPill state={paused ? "draft" : status} />` — "paused live shows as
 * draft-ish slate" — with a separate "Paused" tag in the name column and the
 * row at 0.85 opacity. The pill does *not* keep the live green and merely stop
 * its dot; a paused contest is not taking entries, and the design says so by
 * draining the colour out of it.
 *
 * No new state is introduced for this — `draft` is reused exactly as the
 * handoff reuses it. `toLifecycle` still reports where the contest really is,
 * which is what the tag, the track and the copy read.
 */
export const statusPillState = (contest: LifecycleContest): ContestLifecycleState => {
  const { state, isPaused } = toLifecycle(contest);
  return isPaused ? 'draft' : state;
};

export interface LifecycleChip {
  value: ContestLifecycleState | 'all';
  label: string;
}

/**
 * The Contests list's status chip row.
 *
 * **Cancelled has no chip**, by the decision on CAE-1541: a cancelled contest is
 * noise in a filter row an operator uses to find work, and it is still reachable
 * under All.
 */
export const LIFECYCLE_CHIPS: readonly LifecycleChip[] = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: LIFECYCLE_LABELS.draft },
  { value: 'scheduled', label: LIFECYCLE_LABELS.scheduled },
  { value: 'live', label: LIFECYCLE_LABELS.live },
  { value: 'closed', label: LIFECYCLE_LABELS.closed },
];

/**
 * The Schedule card's four milestones, in order (design-system.md §6
 * "Horizontal milestone track").
 *
 * Four, not five: the prototype's `app.jsx` grew a "Published" step that appears
 * in no README and no screenshot, and is not taken.
 */
export const LIFECYCLE_MILESTONES = ['Created', 'Opens', 'Closes', 'Settled'] as const;

/**
 * How far along the rail the accent line runs, as a fraction.
 *
 * Steps, not gaps between steps: `n` milestones have `n - 1` intervals, so the
 * third of four is two intervals of three — 2/3, not 3/4. Measured off the
 * reference capture, where the blue line ends at 0.657 of the rail with Closes
 * current; 2/3 is the value that produces.
 *
 * Lives here rather than with `MilestoneTrack` so the component's `--prog` and
 * the `progress` a screen reads off `lifecycleTrack` cannot drift apart.
 */
export const trackProgress = (currentIndex: number, total: number): number =>
  total <= 1 ? 0 : Math.min(1, Math.max(0, currentIndex / (total - 1)));

export interface LifecycleTrack {
  steps: readonly string[];
  /** How many leading steps are finished. */
  doneCount: number;
  /**
   * The step in progress, or `null` when none is.
   *
   * Two states genuinely have no current step: a draft or an upcoming contest
   * has finished Created and not started anything else, and a settled one has
   * nothing left. The handoff's own table (`02-contest-overview/README.md`,
   * "Lifecycle states per row") marks those rows `done … future` with no
   * `current` anywhere, and only `current` wears the ring.
   *
   * When it is set it always equals `doneCount` — the step in progress is the
   * one after the last finished one.
   */
  currentIndex: number | null;
  /** 0–1, what `MilestoneTrack` draws its accent line to. */
  progress: number;
}

/**
 * The handoff's "Lifecycle states per row" table, as `[doneCount, current]`:
 *
 * | Lifecycle | Created | Opens | Closes  | Settled |
 * | draft     | done    | future| future  | future  |
 * | scheduled | done    | future| future  | future  |
 * | live      | done    | done  | current | future  |
 * | closed    | done    | done  | done    | current |
 *
 * Read off the *backend status*, not the lifecycle state, because `settling`
 * and `complete` are both `closed` and sit in different places: settling is
 * standing on Settled, complete has finished it.
 */
const trackPosition = (
  contest: LifecycleContest,
): { doneCount: number; current: number | null } => {
  const { state } = toLifecycle(contest);

  if (state === 'live') return { doneCount: 2, current: 2 };

  if (state === 'closed') {
    return underlyingStatus(contest) === 'complete'
      ? { doneCount: LIFECYCLE_MILESTONES.length, current: null }
      : { doneCount: LIFECYCLE_MILESTONES.length - 1, current: LIFECYCLE_MILESTONES.length - 1 };
  }

  // draft, scheduled and cancelled: Created is behind them, nothing is underway.
  return { doneCount: 1, current: null };
};

/**
 * Where a contest sits on the four-step track.
 *
 * The step is read off the *backend status*, not the lifecycle state, because
 * `settling` and `complete` are both `closed` and sit in different places:
 * settling is still on Settled, complete is past it. A paused contest tracks at
 * the step it was paused on — pause stops the clock, it does not move the
 * contest.
 *
 * `cancelled` stops at Created, which is the decision recorded on CAE-1541 — but
 * it is a decision taken under a data limitation, not a statement of fact.
 * `CANCELLABLE_STATUSES` (see `pages/contests/status-transitions.ts`) is
 * `draft`, `upcoming` and `open`, so a contest *can* be cancelled after it
 * opened — and unlike a pause, which records `pausedFromStatus`, a cancellation
 * records nothing about where it happened. `opensAt` being in the past does not
 * settle it either: a draft cancelled a week after its planned open never
 * opened at all.
 *
 * So the track cannot know, and parking it at Created is the reading that
 * invents the least. A contest cancelled out of `open` will under-draw its
 * track. Fixing that properly needs a `cancelledFromStatus` on the API, which is
 * the Schedule card ticket's (CAE-1546) call to make, not this layer's.
 */
export const lifecycleTrack = (contest: LifecycleContest): LifecycleTrack => {
  const { doneCount, current } = trackPosition(contest);

  return {
    steps: LIFECYCLE_MILESTONES,
    doneCount,
    currentIndex: current,
    // The line ends at the furthest dot the contest has reached — the current
    // one while something is underway, otherwise the last finished one.
    progress: trackProgress(current ?? doneCount - 1, LIFECYCLE_MILESTONES.length),
  };
};

/**
 * The five rows the Schedule card's rail draws, in order.
 *
 * One more than `LIFECYCLE_MILESTONES`, which `MilestoneTrack` still uses:
 * **Published** is the manual admin step between creating a contest and its
 * open arriving, and the horizontal four-dot track has nowhere to put it. The
 * labels are the prototype's, including its past tense for the two scheduled
 * moments — a row reading "Opened" with a future-state date is the design's way
 * of saying *this is when it will have opened*.
 */
export const SCHEDULE_RAIL_STEPS = ['Created', 'Published', 'Opened', 'Closed', 'Settled'] as const;

export type ScheduleRailStep = (typeof SCHEDULE_RAIL_STEPS)[number];

export type ScheduleRailState = 'done' | 'current' | 'future';

export interface ScheduleRailMilestone {
  label: ScheduleRailStep;
  state: ScheduleRailState;
  /** The ISO timestamp, or `null` where the contest has no date for it. */
  at: string | null;
  /** What an undated row says instead of an em-dash, where that reads truer. */
  pendingNote?: string;
}

/**
 * The handoff's `Schedule` state table, with the two rows it does not have.
 *
 * `closed` is split because `settling` and `complete` sit in different places —
 * the prototype has one `closed` state and parks the ring on Settled for it,
 * which is right for a contest still settling and wrong for one that has. A
 * `cancelled` contest the prototype never drew parks at Created, as
 * `lifecycleTrack` does: a cancellation records nothing about where it
 * happened, so claiming it reached Opened would be an invention.
 */
type RailKey = Exclude<ContestLifecycleState, 'closed'> | 'settling' | 'complete';

const RAIL_STATES: Record<RailKey, Record<ScheduleRailStep, ScheduleRailState>> = {
  draft: {
    Created: 'done',
    Published: 'future',
    Opened: 'future',
    Closed: 'future',
    Settled: 'future',
  },
  scheduled: {
    Created: 'done',
    Published: 'done',
    Opened: 'future',
    Closed: 'future',
    Settled: 'future',
  },
  live: {
    Created: 'done',
    Published: 'done',
    Opened: 'done',
    Closed: 'current',
    Settled: 'future',
  },
  settling: {
    Created: 'done',
    Published: 'done',
    Opened: 'done',
    Closed: 'done',
    Settled: 'current',
  },
  complete: {
    Created: 'done',
    Published: 'done',
    Opened: 'done',
    Closed: 'done',
    Settled: 'done',
  },
  cancelled: {
    Created: 'done',
    Published: 'future',
    Opened: 'future',
    Closed: 'future',
    Settled: 'future',
  },
};

/**
 * What the Schedule card's vertical rail shows — the five milestones, where the
 * contest has got to, and when each one happens.
 *
 * ISO strings rather than `Date`s: every caller hands these to a formatter in
 * `utils/time`, which takes both, and the strings pass through untouched from
 * the API.
 *
 * **Settled needs no new backend field**, which answers the open question on
 * CAE-1546. A `complete` contest's `statusChangedAt` *is* its settlement time —
 * completion is the transition that recorded it. A `settling` one has not
 * settled, so it gets `null`; that is the ticket's own instruction, and
 * inventing a date from `closesAt` would be the "stats slop" the design rules
 * out.
 *
 * **Published has no timestamp at all.** Nothing in the contest payload records
 * when a draft was published — `statusChangedAt` has moved on by the time it is
 * live — so a published row shows an em-dash rather than a guess, and an
 * unpublished one says what it is waiting for. Same rule as Settled, and the
 * field to add if design wants the date.
 *
 * A **cancelled** contest keeps its `opensAt` and `closesAt`. They are real
 * scheduled moments, the rail draws a future row in its own quieter ink, and
 * dropping them contradicted the health strip beside it — which deliberately
 * keeps reporting the entries a cancelled contest genuinely took.
 *
 * Read through `underlyingStatus` so a pause does not hide where the contest
 * really is. Note that this is *not* what keeps a paused contest from reporting
 * its pause as a settlement — `statusChangedAt` on a paused row is when it was
 * held, so a contest paused *from* `complete` would date Settled wrongly. What
 * actually protects that today is `PAUSABLE_STATUSES`, which stops at
 * `settling`: there is no pausing a contest that has already completed. Make
 * `complete` pausable and this needs a real settled timestamp first.
 */
export const scheduleRail = (contest: LifecycleContest): readonly ScheduleRailMilestone[] => {
  const status = underlyingStatus(contest);
  const { state } = toLifecycle(contest);
  // `closed` is the only state that splits — read off the backend status, as
  // `lifecycleTrack` does, because settling and complete sit in different rows.
  const states =
    RAIL_STATES[state === 'closed' ? (status === 'complete' ? 'complete' : 'settling') : state];

  const at: Record<ScheduleRailStep, string | null> = {
    Created: contest.createdAt,
    Published: null,
    Opened: contest.opensAt,
    Closed: contest.closesAt,
    Settled: status === 'complete' ? contest.statusChangedAt : null,
  };

  /*
   * Only a contest someone can still publish is waiting on a publish.
   *
   * `CANCELLABLE_STATUSES` is `draft`, `upcoming` and `open`, so two of the
   * three were published before they were cancelled — and `cancelled` is
   * terminal, so even the draft case has nobody left to act. The rail parks a
   * cancelled contest at Created because a cancellation records nothing about
   * where it happened; that is an honest absence, where "Awaiting admin action"
   * would be a false instruction.
   */
  const awaitingPublish = state === 'draft';

  return SCHEDULE_RAIL_STEPS.map((label) => ({
    label,
    state: states[label],
    at: at[label],
    // Only the one step a person has to take says so. The others are undated
    // because nothing has recorded them, which is not the same as waiting.
    ...(label === 'Published' && awaitingPublish ? { pendingNote: 'Awaiting admin action' } : {}),
  }));
};

const parseDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
};

export interface LifecycleMilestone {
  /** The step's name on the track, e.g. "Opens". */
  label: string;
  /** Its position in `LIFECYCLE_MILESTONES`, so a caller can line it up. */
  index: number;
  at: Date;
  /** Signed: negative once the moment has passed but the status has not caught up. */
  inMs: number;
}

/**
 * The next dated thing to happen to this contest, or `null` when there isn't
 * one.
 *
 * `inMs` is signed on purpose. The `open → in_play` transition is a cron
 * projection that can lag `closes_at` by up to a minute (see CONTEXT.md,
 * "Status vs. cutoff"), so a live contest genuinely can be a few seconds past
 * its own close. Clamping that to zero here would hide the lag from the screen
 * that wants to show it; `formatDuration` floors at zero when it renders.
 *
 * A settling contest returns `null`: settlement finishes when the feed says so,
 * and inventing an ETA would be the "stats slop" the design rules out.
 */
export const nextMilestone = (contest: LifecycleContest, now: Date): LifecycleMilestone | null => {
  const { state } = toLifecycle(contest);

  // `in_play` has no dated next milestone. It is not a contest still running
  // towards its close — it is the *projection of closure*: the cron flips
  // `open → in_play` once `closesAt <= now`
  // (`apps/functions/src/functions/contest-status-tick.ts`, and CONTEXT.md
  // "Status vs. cutoff"), and the contest then sits there for as long as the
  // fixtures take. Its close is always behind it, by hours, so counting down to
  // it read "Closes in 0m" for the entire window — a confident wrong number
  // rather than the ~60s cron lag the clamp below was written for. What it is
  // actually waiting on is the fixtures finishing, which this layer has no date
  // for, so it answers nothing rather than inventing one.
  const pending =
    state === 'draft' || state === 'scheduled'
      ? ({ label: 'Opens', index: 1, at: parseDate(contest.opensAt) } as const)
      : state === 'live' && underlyingStatus(contest) !== 'in_play'
        ? ({ label: 'Closes', index: 2, at: parseDate(contest.closesAt) } as const)
        : null;

  if (!pending?.at) return null;

  return { ...pending, at: pending.at, inMs: pending.at.getTime() - now.getTime() };
};

export interface FeaturedMilestone {
  /**
   * The verb half — "Opens", "Closes" — which the Schedule card sets in
   * `--ink-3` at weight 500 beside a full-ink duration.
   *
   * When there is nothing to count down to it carries the whole sentence
   * instead ("Awaiting settlement", "Paused"), because there is no second half
   * for it to be the verb of.
   */
  verb: string;
  /** The countdown beside the verb, or `null` when nothing is pending. */
  duration: string | null;
  /**
   * The moment itself, for the card's stacked time-over-date — non-null
   * exactly when `duration` is. A state that refuses to count down refuses to
   * date itself too: pairing "Awaiting settlement" with a timestamp would read
   * as a promise of when it will finish.
   */
  at: Date | null;
}

/**
 * What a contest is waiting on, in the parts a screen draws separately.
 *
 * `settling` and `complete` are both `closed` and read differently: settlement
 * is either still happening or it is done. A cancelled contest is waiting on
 * nothing and says so.
 *
 * When the date a countdown needs is missing, this falls back to the state's own
 * name rather than writing "Opens in 0m", which would read as imminent.
 */
export const featuredMilestone = (contest: LifecycleContest, now: Date): FeaturedMilestone => {
  const idle = (verb: string): FeaturedMilestone => ({ verb, duration: null, at: null });
  const { state, isPaused } = toLifecycle(contest);

  // Pause wins here exactly as it does in `timeInStatus`. A paused contest is
  // not counting down to anything — the backend is not advancing it — so a
  // headline reading "Closes in 5h 0m" would promise a close that will not
  // happen on time.
  if (isPaused) return idle('Paused');

  if (state === 'closed') {
    return idle(underlyingStatus(contest) === 'complete' ? 'Settled' : 'Awaiting settlement');
  }

  if (state === 'cancelled') return idle(LIFECYCLE_LABELS.cancelled);

  const milestone = nextMilestone(contest, now);
  if (!milestone) return idle(LIFECYCLE_LABELS[state]);

  // A draft's `opensAt` is a *plan*, not an appointment: nothing is going to
  // open an unpublished contest when it arrives. Once it has passed, counting
  // down to it reads "Opens in 0m" — the imminent-sounding string the fallback
  // above exists to avoid, which it otherwise only catches for a *missing*
  // date. A `scheduled` contest keeps counting, because there 0m is true: the
  // open projection is a cron that lags by up to a minute.
  if (state === 'draft' && milestone.inMs <= 0) return idle(LIFECYCLE_LABELS.draft);

  return { verb: milestone.label, duration: formatDuration(milestone.inMs), at: milestone.at };
};

/**
 * The headline on the Contests list's featured card — the one sentence that says
 * what this contest is waiting on.
 *
 * The sentence `featuredMilestone`'s parts make, rather than a second derivation
 * of the same rules: the list reads this and the Contest Overview's Schedule
 * card reads the parts, and the two cannot disagree about when a contest counts
 * down.
 */
export const featuredCopy = (contest: LifecycleContest, now: Date): string => {
  const { verb, duration } = featuredMilestone(contest, now);
  return duration ? `${verb} in ${duration}` : verb;
};

/**
 * How long this contest has been in its current status — the Contest Overview's
 * age chip.
 *
 * Always elapsed, for every state. This is the half of "time in status" the
 * handoff describes in §6; `timeInStatus` below is the other, different half.
 */
export const timeInStatusMs = (contest: LifecycleContest, now: Date): number => {
  const changedAt = parseDate(contest.statusChangedAt);
  return changedAt ? Math.max(0, now.getTime() - changedAt.getTime()) : 0;
};

export interface TimeInStatus {
  ms: number;
  /** `ms` through `formatDuration`, so every caller reads the same shape. */
  text: string;
  /** The words under the duration, e.g. "until open". */
  qualifier: string;
}

/**
 * The Contests list's time cell.
 *
 * Deliberately **not** `timeInStatusMs`. Per `01-contests-list`, a contest that
 * is waiting on a known moment counts down to it — an upcoming contest to its
 * open, a live one to its close — and only a contest waiting on nothing reports
 * how long it has been waiting. Conflating the two is the mistake CAE-1541 was
 * written to prevent: an operator scanning the column wants "4h until this needs
 * me", not "4h since someone pressed publish".
 *
 * Pause always wins. A paused contest is not counting down to anything, however
 * it got there.
 */
export const timeInStatus = (contest: LifecycleContest, now: Date): TimeInStatus => {
  const { state, isPaused } = toLifecycle(contest);

  const elapsed = (qualifier: string): TimeInStatus => {
    const ms = timeInStatusMs(contest, now);
    return { ms, text: formatDuration(ms), qualifier };
  };

  if (isPaused) return elapsed('since pause');

  switch (state) {
    case 'draft':
      return elapsed('in draft');
    case 'closed':
      // Both `settling` and `complete` land here, and only one of them has
      // actually settled — the same split `featuredCopy` and the track make.
      return elapsed(underlyingStatus(contest) === 'complete' ? 'settled' : 'settling');
    case 'cancelled':
      return elapsed('since cancel');
    case 'scheduled':
    case 'live': {
      const milestone = nextMilestone(contest, now);
      // No date to count down to — an `in_play` contest, whose close is behind
      // it; an open-ended one; or a row the API came back thin on. Say what we
      // do know rather than counting down to nothing. `in progress` is the word
      // the current Contests page already uses for `in_play`, so this invents no
      // new vocabulary.
      if (!milestone) {
        return elapsed(underlyingStatus(contest) === 'in_play' ? 'in progress' : 'in status');
      }
      // Clamped, unlike `nextMilestone`'s signed `inMs`. `ms` and `text` are
      // documented to be the same number, and a cron that has not caught up
      // yet — routinely a minute on the `open → in_play` projection — would
      // otherwise hand a caller `-3600000` beside the string `0m`. A screen
      // that wants the lag reads `nextMilestone` directly, where the sign is
      // the point.
      const remaining = Math.max(0, milestone.inMs);
      return {
        ms: remaining,
        text: formatDuration(remaining),
        qualifier: state === 'scheduled' ? 'until open' : 'remaining',
      };
    }
  }
};
